import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { HttpStatusCode } from '@angular/common/http';
import { Router } from '@angular/router';

import { LoadingSpinnerComponent, TranslationService } from 'lipp-components';

import {
  McpAccessService,
  McpAuthorizationRequest,
  McpConsentContext,
} from 'src/services/mcp-access.service';
import { LearningSuiteService } from 'src/services/learning-suite.service';
import { Utility } from 'src/services/utility.service';

import { ErrorMessage } from 'src/models/Result';

import { LippCommonModule } from 'src/components/common.module';
import { InfoPageModule } from 'src/components/info-page/info-page.module';

/**
 * Zustimmung für KI-Anwendungen wie Claude oder ChatGPT, die sich über den MCP-Dienst mit dem
 * Lipp Portal verbinden wollen (LIPP-1500).
 *
 * Die alte Lipp API prüft den Client unter /Oidc/Authorize und leitet hierher weiter, weil nur das
 * Portal den Login-JWT kennt. Ist der Benutzer eingeloggt, zeigt die Seite, wer was lesen möchte;
 * „Zugriff erlauben“ holt einen Einmal-Code und leitet zur Anwendung zurück. Ohne Login wird die
 * Anfrage gemerkt und nach dem Login fortgesetzt (wie /sso/authorize).
 *
 * Liegt bewusst AUSSERHALB von PagesGuard: der lädt sonst das komplette Portal und leitet ohne
 * Login ohne Rücksprung auf /auth/login.
 */
@Component({
  selector: 'app-oauth-consent',
  templateUrl: './oauth-consent.component.html',
  styleUrls: ['./oauth-consent.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [LippCommonModule, InfoPageModule, LoadingSpinnerComponent],
})
export class OAuthConsentComponent implements OnInit {
  public isLoading: boolean = true;
  public isSubmitting: boolean = false;

  public context?: McpConsentContext;

  public errorTitle?: string;
  public errorDescription?: string;

  private request?: McpAuthorizationRequest;

  constructor(
    private router: Router,
    private translationService: TranslationService,
    private mcpAccessService: McpAccessService
  ) {}

  public ngOnInit(): void {
    // Die Freigabe ist ein einziger Klick: nie in einem fremden Rahmen anbieten (Clickjacking).
    if (window.top !== window.self) {
      this.errorTitle = this.translationService.translate('OAuthConsent.ErrorTitle');
      this.errorDescription = this.translationService.translate('OAuthConsent.Framed');
      this.isLoading = false;
      return;
    }

    const search = window.location.search;

    // Bis zum Rücksprung merken: läuft der JWT ab (401 → Login), setzt die LoginComponent die
    // Anfrage danach fort.
    LearningSuiteService.rememberPendingSsoAuthorize(search, '/oauth/consent');

    if (!Utility.hasAuthToken) {
      this.router.navigateByUrl('/auth/login');
      return;
    }

    const params = new URLSearchParams(search);
    this.request = {
      clientId: params.get('client_id'),
      redirectUri: params.get('redirect_uri'),
      responseType: params.get('response_type'),
      scope: params.get('scope'),
      state: params.get('state'),
      codeChallenge: params.get('code_challenge'),
      codeChallengeMethod: params.get('code_challenge_method'),
      resource: params.get('resource'),
    };

    this.mcpAccessService.getConsentContext(this.request).subscribe(
      (context) => {
        this.context = context;
        this.isLoading = false;
      },
      (errorMessages: ErrorMessage[]) => this.showError(errorMessages)
    );
  }

  public allow(): void {
    if (!this.request || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;

    this.mcpAccessService.issueAuthorizationCode(this.request).subscribe(
      (redirectUrl) => {
        LearningSuiteService.forgetPendingSsoAuthorize();
        window.location.replace(redirectUrl);
      },
      (errorMessages: ErrorMessage[]) => {
        this.isSubmitting = false;
        this.showError(errorMessages);
      }
    );
  }

  public deny(): void {
    if (!this.context) {
      return;
    }

    LearningSuiteService.forgetPendingSsoAuthorize();
    window.location.replace(this.context.denyRedirectUrl);
  }

  private showError(errorMessages: ErrorMessage[]): void {
    // 401: ApiService leitet bereits auf /auth/login um – Anfrage gemerkt lassen.
    if (errorMessages[0].key === `Messages.HttpError.${HttpStatusCode.Unauthorized}`) {
      return;
    }

    LearningSuiteService.forgetPendingSsoAuthorize();

    this.context = undefined;
    this.errorTitle = this.translationService.translate('OAuthConsent.ErrorTitle');
    this.errorDescription = this.translationService.translate(
      errorMessages[0].key,
      errorMessages[0].textParameters
    );
    this.isLoading = false;
  }
}
